import { AlertCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table'
import { Progress } from '../ui/progress'
import { Button } from '../ui/button'
import { SupplierStatusBadgeV2 } from './SupplierStatusBadgeV2'
import { CampaignStatusBadge } from './CampaignStatusBadge'
import type { SupplierProfileCampaign } from '@/lib/api'
import { useIsMobile } from '../ui/use-mobile'
import { formatRelative, formatDateMedium } from '@/lib/formatDate'

function canRequestComplement(c: SupplierProfileCampaign): boolean {
  // Bug 1 follow-up : pas de complément possible sur une campagne fermée/archivée.
  if (c.campaignStatus === 'CLOSED' || c.campaignStatus === 'ARCHIVED') return false
  return c.submissionStatus === 'SUBMITTED' || c.submissionStatus === 'RESUBMITTED'
}

// Cap UI : afficher au plus 50 campagnes (le back renvoie jusqu'à
// MAX_CAMPAIGNS_PER_PROFILE=500). Au-delà, surfacer un lien désactivé
// « Voir les N plus anciennes » (vrai accès archive = feature future).
const MAX_DISPLAYED_CAMPAIGNS = 50

type OpenCampaignOptions = { campaignSupplierId?: string }

type Props = {
  campaigns: SupplierProfileCampaign[]
  // Story 3.6 — signature étendue : le 2e argument permet de propager
  // `campaignSupplierId` pour ouvrir directement la vue campagne × fournisseur.
  // Rétrocompat : appelable sans 2e arg (le détail fournisseur ne s'auto-ouvre
  // pas et la vue tombe sur la liste fournisseurs du dashboard).
  onOpenCampaign?: (campaignId: string, options?: OpenCampaignOptions) => void
  onRequestComplement?: (campaign: SupplierProfileCampaign) => void
}

function FieldsCompletion({ filled, total }: { filled: number; total: number }) {
  if (total === 0) {
    return <span className="text-muted-foreground">—</span>
  }
  // Clamp [0,100] : filled>total (drift serveur) ne doit pas produire
  // 120% ni casser la barre Progress.
  const pct = Math.max(0, Math.min(100, Math.round((filled / total) * 100)))
  return (
    <div className="flex min-w-[8rem] items-center gap-2">
      <Progress value={pct} className="h-1.5 w-20" aria-label={`${filled} sur ${total} champs requis remplis`} />
      <span className="tabular-nums text-xs text-muted-foreground">
        {filled} / {total}
      </span>
    </div>
  )
}

function OverdueIndicator({ isOverdue }: { isOverdue: boolean }) {
  if (!isOverdue) return null
  return (
    <span
      className="ml-2 inline-flex items-center gap-1 text-xs font-medium text-destructive"
    >
      <AlertCircle className="h-3 w-3" aria-hidden="true" />
      En retard
    </span>
  )
}

function CampaignNameLink({
  campaignId,
  campaignSupplierId,
  campaignName,
  onOpenCampaign,
}: {
  campaignId: string
  // P-7 — optionnel : peut être absent sur des données antérieures au champ.
  campaignSupplierId?: string
  campaignName: string
  onOpenCampaign?: (id: string, options?: OpenCampaignOptions) => void
}) {
  if (!onOpenCampaign) {
    return <span className="font-medium">{campaignName}</span>
  }
  return (
    <button
      type="button"
      onClick={() => onOpenCampaign(campaignId, campaignSupplierId ? { campaignSupplierId } : undefined)}
      className="font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {campaignName}
    </button>
  )
}

function CampaignRow({
  c,
  onOpenCampaign,
  onRequestComplement,
}: {
  c: SupplierProfileCampaign
  onOpenCampaign?: (id: string, options?: OpenCampaignOptions) => void
  onRequestComplement?: (campaign: SupplierProfileCampaign) => void
}) {
  return (
    <TableRow>
      <TableCell>
        <div className="flex items-center">
          <CampaignNameLink
            campaignId={c.campaignId}
            campaignSupplierId={c.campaignSupplierId}
            campaignName={c.campaignName}
            onOpenCampaign={onOpenCampaign}
          />
          <OverdueIndicator isOverdue={c.isOverdue} />
        </div>
      </TableCell>
      <TableCell>
        <CampaignStatusBadge status={c.campaignStatus} />
      </TableCell>
      <TableCell>
        <SupplierStatusBadgeV2 status={c.submissionStatus} />
      </TableCell>
      <TableCell className="text-muted-foreground">
        {formatDateMedium(c.dueAt)}
      </TableCell>
      <TableCell className="text-muted-foreground">
        {formatRelative(c.submittedAt)}
      </TableCell>
      <TableCell>
        <FieldsCompletion filled={c.requiredFilled} total={c.requiredTotal} />
      </TableCell>
      <TableCell className="text-right">
        {canRequestComplement(c) && onRequestComplement && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation()
              onRequestComplement(c)
            }}
          >
            Demander un complément
          </Button>
        )}
      </TableCell>
    </TableRow>
  )
}

function CampaignCard({
  c,
  onOpenCampaign,
  onRequestComplement,
}: {
  c: SupplierProfileCampaign
  onOpenCampaign?: (id: string, options?: OpenCampaignOptions) => void
  onRequestComplement?: (campaign: SupplierProfileCampaign) => void
}) {
  return (
    <div className={cn('rounded-lg border p-4 bg-card space-y-3')}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center flex-wrap gap-2">
            <CampaignNameLink
              campaignId={c.campaignId}
              campaignSupplierId={c.campaignSupplierId}
              campaignName={c.campaignName}
              onOpenCampaign={onOpenCampaign}
            />
            <OverdueIndicator isOverdue={c.isOverdue} />
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <CampaignStatusBadge status={c.campaignStatus} />
            <SupplierStatusBadgeV2 status={c.submissionStatus} />
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-1 text-xs text-muted-foreground">
        <div>Échéance : {formatDateMedium(c.dueAt)}</div>
        <div>Soumis : {formatRelative(c.submittedAt)}</div>
      </div>
      <FieldsCompletion filled={c.requiredFilled} total={c.requiredTotal} />
      {canRequestComplement(c) && onRequestComplement && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="w-full"
          onClick={(e) => {
            e.stopPropagation()
            onRequestComplement(c)
          }}
        >
          Demander un complément
        </Button>
      )}
    </div>
  )
}

function OlderCampaignsNotice({ hidden }: { hidden: number }) {
  if (hidden <= 0) return null
  return (
    <div className="mt-3 text-center text-xs text-muted-foreground">
      <button
        type="button"
        disabled
        title="Bientôt disponible"
        className="underline decoration-dotted underline-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
      >
        Voir les {hidden} plus anciennes
      </button>
    </div>
  )
}

export function CampaignHistoryTable({
  campaigns,
  onOpenCampaign,
  onRequestComplement,
}: Props) {
  const isMobile = useIsMobile()
  const visible = campaigns.slice(0, MAX_DISPLAYED_CAMPAIGNS)
  const hidden = Math.max(0, campaigns.length - MAX_DISPLAYED_CAMPAIGNS)

  if (isMobile) {
    return (
      <div className="flex flex-col gap-3">
        {visible.map((c) => (
          <CampaignCard
            key={c.campaignSupplierId}
            c={c}
            onOpenCampaign={onOpenCampaign}
            onRequestComplement={onRequestComplement}
          />
        ))}
        <OlderCampaignsNotice hidden={hidden} />
      </div>
    )
  }

  return (
    <div className="rounded-lg border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Campagne</TableHead>
            <TableHead>Statut campagne</TableHead>
            <TableHead>Statut soumission</TableHead>
            <TableHead>Échéance</TableHead>
            <TableHead>Soumis le</TableHead>
            <TableHead>Champs remplis</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {visible.map((c) => (
            <CampaignRow
              key={c.campaignSupplierId}
              c={c}
              onOpenCampaign={onOpenCampaign}
              onRequestComplement={onRequestComplement}
            />
          ))}
        </TableBody>
      </Table>
      <OlderCampaignsNotice hidden={hidden} />
    </div>
  )
}
